import type { WalletBenefit, WalletCard } from "../wallet/types";
import { isTrustedForScoring, isTrustedForMainList, confidenceTier } from "./bucketize";
import type { Profile, SpendScenario } from "./profile";

type CardScore = {
  card: WalletCard;
  score: number;
  reasons: string[];
  contributions: ReasonContribution[];
};

const SCENARIO_WEIGHTS: Record<SpendScenario, Record<string, number>> = {
  dining: { dining: 4, shopping: 2 },
  delivery_rideshare: { shopping: 3, dining: 2 },
  grocery: { shopping: 4 },
  fuel: { shopping: 2 },
  online_shop: { shopping: 4 },
  reimburse: { shopping: 1 }
};

const FREQUENCY_WEIGHTS: Record<string, number> = {
  none: 0,
  low: 1,
  mid: 2,
  high: 3
};

function confidenceWeight(c: string): number {
  if (c === "high") return 1;
  if (c === "medium") return 0.5;
  return 0;
}

// 排序权重：让 medium 能参与排名（带折扣），避免数据稀疏时全 0 平局
function rankingWeight(c: string): number {
  if (c === "high") return 1;
  if (c === "medium") return 0.4;
  return 0;
}

export type ReasonContribution = {
  benefit: WalletBenefit;
  contribution: number;
};

export type DefaultCardResult = {
  card: WalletCard | null;
  score: number;
  reason: string;
  payHint: string;
  exceptions: Exception[];
  noteWhenWeak: string | null;
  trustedBenefitCount: number;
  totalOwnedCardCount: number;
  pinned: boolean;
  topContributions: ReasonContribution[];
};

export type Exception = {
  scenario: string;
  emoji: string;
  card: WalletCard;
  benefit: WalletBenefit;
};

function payHint(profile: Profile): string {
  switch (profile.pay) {
    case "apple":
      return "iPhone 双击侧键，用这张卡的 Apple Pay";
    case "wechat":
      return "微信钱包默认卡设这张";
    case "alipay":
      return "支付宝默认付款方式设这张";
    case "unionpay":
      return "云闪付默认卡设这张";
    case "card":
      return "钱包里抽这张";
  }
}

export function computeDefaultCard(
  selectedCards: WalletCard[],
  allBenefits: WalletBenefit[],
  profile: Profile
): DefaultCardResult {
  if (selectedCards.length === 0) {
    return {
      card: null,
      score: 0,
      reason: "还没添加卡",
      payHint: "",
      exceptions: [],
      noteWhenWeak: null,
      trustedBenefitCount: 0,
      totalOwnedCardCount: 0,
      pinned: false,
      topContributions: []
    };
  }

  const benefitsByCard = new Map<string, WalletBenefit[]>();
  for (const b of allBenefits) {
    if (!benefitsByCard.has(b.cardId)) benefitsByCard.set(b.cardId, []);
    benefitsByCard.get(b.cardId)!.push(b);
  }

  const scenarioWeight: Record<string, number> = {};
  for (const sc of profile.scenarios) {
    const map = SCENARIO_WEIGHTS[sc] || {};
    for (const [cat, w] of Object.entries(map)) {
      scenarioWeight[cat] = (scenarioWeight[cat] || 0) + w;
    }
  }
  if (profile.scenarios.length === 0) {
    scenarioWeight["shopping"] = 2;
    scenarioWeight["dining"] = 2;
  }
  const flightW = FREQUENCY_WEIGHTS[profile.flights] || 0;
  const hotelW = FREQUENCY_WEIGHTS[profile.hotels] || 0;

  let totalTrusted = 0;

  const scores: CardScore[] = selectedCards.map(card => {
    const list = benefitsByCard.get(card.id) || [];
    let s = 0;
    const reasons: string[] = [];
    const contributions: ReasonContribution[] = [];
    for (const b of list) {
      const tier = confidenceTier(b);
      if (tier !== "high" && tier !== "medium") continue;
      if (tier === "high") totalTrusted += 1;
      const cw = rankingWeight(b.confidence);
      const catW = scenarioWeight[b.category] || 0;
      let contribution = catW * cw;
      if (b.category === "airport_lounge" || b.category === "airport_transfer") {
        contribution += flightW * cw * 1.5;
      }
      if (b.category === "hotel") {
        contribution += hotelW * cw * 1.5;
      }
      if (b.kind === "cashback_lottery" || b.kind === "discount") {
        contribution += 1 * cw;
      }
      s += contribution;
      contributions.push({ benefit: b, contribution });
      if (contribution > 1.5) {
        reasons.push(b.title);
      }
    }
    contributions.sort((a, b) => b.contribution - a.contribution);
    return { card, score: s, reasons, contributions };
  });

  scores.sort((a, b) => b.score - a.score);
  const algoBest = scores[0];

  const pinned = profile.pinnedDefaultCardId
    ? selectedCards.find(c => c.id === profile.pinnedDefaultCardId) ?? null
    : null;
  const best = pinned
    ? scores.find(s => s.card.id === pinned.id) ?? { card: pinned, score: 0, reasons: [], contributions: [] as ReasonContribution[] }
    : algoBest;
  const isPinned = !!pinned && pinned.id === best.card.id;

  const exceptions = pickExceptions(selectedCards, benefitsByCard, profile, best.card);

  const noteWhenWeak = isPinned
    ? null
    : best.score < 1
    ? "你的卡组合里没有特别强的高置信日常返现锚点。可以先用这张兜底；当我们核实更多权益规则后，推荐会自动更准。"
    : null;

  const reason = isPinned
    ? "你手动把它设成了默认卡"
    : best.reasons.length
    ? `因为它有：${best.reasons.slice(0, 2).join(" / ")}`
    : totalTrusted === 0
    ? "在你选的卡里综合分最高（暂无已核实的高置信权益作为推荐依据）"
    : "在你选的卡里综合分最高";

  return {
    card: best.card,
    score: best.score,
    reason,
    payHint: payHint(profile),
    exceptions,
    noteWhenWeak,
    trustedBenefitCount: totalTrusted,
    totalOwnedCardCount: selectedCards.length,
    pinned: isPinned,
    topContributions: best.contributions.filter(c => c.contribution > 0).slice(0, 3)
  };
}

const SCENARIO_LABELS: Array<{ category: string; emoji: string; label: string }> = [
  { category: "airport_lounge", emoji: "✈️", label: "去机场" },
  { category: "airport_transfer", emoji: "🚖", label: "接送机" },
  { category: "hotel", emoji: "🏨", label: "订酒店" },
  { category: "dining", emoji: "🍜", label: "吃饭" },
  { category: "shopping", emoji: "🛒", label: "购物" },
  { category: "miles", emoji: "🛫", label: "攒里程" },
  { category: "insurance", emoji: "🛡️", label: "保险" }
];

function pickExceptions(
  selectedCards: WalletCard[],
  benefitsByCard: Map<string, WalletBenefit[]>,
  profile: Profile,
  defaultCard: WalletCard
): Exception[] {
  const out: Exception[] = [];

  for (const { category, emoji, label } of SCENARIO_LABELS) {
    if (category === "airport_lounge" || category === "airport_transfer") {
      if (profile.flights === "none") continue;
    }
    if (category === "hotel") {
      if (profile.hotels === "none") continue;
    }

    let best: { card: WalletCard; benefit: WalletBenefit; score: number } | null = null;
    for (const card of selectedCards) {
      if (card.id === defaultCard.id) continue;
      const list = benefitsByCard.get(card.id) || [];
      for (const b of list) {
        if (b.category !== category) continue;
        if (!isTrustedForMainList(b)) continue;
        const score = (b.estimateValueCny || 0) * confidenceWeight(b.confidence);
        if (!best || score > best.score) {
          best = { card, benefit: b, score };
        }
      }
    }
    if (!best) continue;
    if (best.score < 30) continue;

    out.push({ scenario: label, emoji, card: best.card, benefit: best.benefit });
    if (out.length >= 5) break;
  }

  return out;
}
